import { XMarkIcon } from '@heroicons/react/24/outline';
import { getSolidStatusBgColor } from '@odo/shared-ui';
import React, { useState } from 'react';

/** The server caps a category list at 20 entries. */
const MAX_CATEGORIES = 20;

interface CategoryFieldProps {
    value: string[];
    onChange: (next: string[]) => void;
    /** Categories already in use across this content type, offered as one-click adds. */
    suggestions: string[];
    disabled?: boolean;
}

/** Tag input over the categories already in use, so the taxonomy doesn't
 * fragment into "Bonding" / "bonding" / "Bonding ". */
export const CategoryField: React.FC<CategoryFieldProps> = ({
    value,
    onChange,
    suggestions,
    disabled = false
}) => {
    const [entry, setEntry] = useState('');

    const add = (raw: string) => {
        const category = raw.trim();
        if (!category || value.length >= MAX_CATEGORIES) return;
        // Case-insensitive match so an existing category is reused rather than
        // duplicated with different capitalisation.
        const existing = value.find((v) => v.toLowerCase() === category.toLowerCase());
        if (existing) {
            setEntry('');
            return;
        }
        onChange([...value, category]);
        setEntry('');
    };

    const remove = (category: string) => onChange(value.filter((v) => v !== category));

    const unused = suggestions.filter(
        (suggestion) => !value.some((v) => v.toLowerCase() === suggestion.toLowerCase())
    );

    return (
        <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-gray-700" htmlFor="resource-category">
                Categories
            </label>

            {/* Selected: the same solid colour-hashed pills the read side
                renders, so the author sees exactly what the list will show. */}
            {value.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    {value.map((category) => (
                        <span
                            key={category}
                            className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-md text-[11px] font-semibold text-white ${getSolidStatusBgColor(category)}`}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
                            {category}
                            <button
                                type="button"
                                onClick={() => remove(category)}
                                disabled={disabled}
                                aria-label={`Remove ${category}`}
                                className="text-white/70 hover:text-white transition-colors disabled:opacity-50"
                            >
                                <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-2">
                <input
                    id="resource-category"
                    value={entry}
                    onChange={(event) => setEntry(event.target.value)}
                    onKeyDown={(event) => {
                        // Enter adds a category; it must not submit the form.
                        if (event.key === 'Enter' || event.key === ',') {
                            event.preventDefault();
                            add(entry);
                        }
                    }}
                    disabled={disabled || value.length >= MAX_CATEGORIES}
                    placeholder="Add a category and press Enter"
                    className="appearance-none block flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 disabled:bg-gray-50"
                />
                <button
                    type="button"
                    onClick={() => add(entry)}
                    disabled={disabled || !entry.trim() || value.length >= MAX_CATEGORIES}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Add
                </button>
            </div>

            {/* In use: a framed picker of the existing taxonomy. Each chip
                carries its category's colour dot and clicks straight in. */}
            {unused.length > 0 && (
                <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-400">
                        Already in use — click to add
                    </span>
                    <div className="flex gap-1.5 flex-wrap max-h-24 overflow-y-auto">
                        {unused.map((suggestion) => (
                            <button
                                key={suggestion}
                                type="button"
                                onClick={() => add(suggestion)}
                                disabled={disabled || value.length >= MAX_CATEGORIES}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                <span
                                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getSolidStatusBgColor(suggestion)}`}
                                />
                                {suggestion}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryField;
